import { createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../../Common/Interceptors/AxiosConfig";
import logger from "../../../../utils/logger";

const initialState = {
  isLoading: false,
  error: null,
  wallet: null,
  allWallets: [], // New Dashboard state
  transactions: [],
  pagination: {
    total: 0,
    page: 1,
    pages: 1
  }
};

const walletSlice = createSlice({
  name: "wallet",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setWallet: (state, action) => {
      state.wallet = action.payload;
    },
    setAllWallets: (state, action) => {
      state.allWallets = action.payload;
    },
    setTransactions: (state, action) => {
      state.transactions = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = action.payload;
    },
    resetWallet: (state) => {
      return initialState;
    }
  }
});

export const { 
  setLoading, 
  setError, 
  setWallet, 
  setAllWallets, 
  setTransactions, 
  setPagination, 
  resetWallet 
} = walletSlice.actions;

/**
 * Fetch all authorized wallets (Dashboard)
 */
export const fetchAllWallets = () => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.get('/wallets/all');
    if (response.data?.success) {
      dispatch(setAllWallets(response.data.data));
    }
  } catch (error) {
    logger.error('Failed to fetch all wallets', error);
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Fetch specific wallet details
 */
export const fetchWalletDetail = (walletId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.get(`/wallets/${walletId}`);
    if (response.data?.success) {
      dispatch(setWallet(response.data.data));
    }
  } catch (error) {
    logger.error('Failed to fetch wallet detail', error);
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Fetch wallet balance (WORKSPACE or USER)
 */
export const getMyWallet = (id, type = 'WORKSPACE') => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const queryParam = type === 'WORKSPACE' ? `workspaceId=${id}` : `type=USER`;
    const response = await axiosInstance.get(`/wallets/my-wallet?${queryParam}`);
    if (response.data?.success) {
      dispatch(setWallet(response.data.data));
    }
  } catch (error) {
    logger.error('Failed to fetch wallet', error);
    dispatch(setError(error.response?.data?.message || 'Failed to fetch wallet'));
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Fetch wallet transactions
 */
export const getWalletTransactions = (params = {}) => async (dispatch) => {
  const { walletId, workspaceId, type, page = 1 } = params;
  dispatch(setLoading(true));
  try {
    let query = `page=${page}`;
    if (walletId) query += `&walletId=${walletId}`;
    else if (type === 'USER') query += `&type=USER`;
    else if (workspaceId) query += `&workspaceId=${workspaceId}`;

    const response = await axiosInstance.get(`/wallets/transactions?${query}`);
    if (response.data?.success) {
      dispatch(setTransactions(response.data.data.items));
      dispatch(setPagination(response.data.data.pagination));
    }
  } catch (error) {
    logger.error('Failed to fetch transactions', error);
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Recharge wallet
 */
export const rechargeWallet = (data) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.post('/wallets/recharge', data);
    if (response.data?.success) {
      dispatch(setWallet(response.data.data));
      // Refresh transactions after recharge
      if (data.type === 'USER') {
        dispatch(getWalletTransactions({ type: 'USER' }));
      } else {
        dispatch(getWalletTransactions({ workspaceId: data.workspaceId }));
      }
      return { success: true };
    }
  } catch (error) {
    logger.error('Recharge failed', error);
    dispatch(setError(error.response?.data?.message || 'Recharge failed'));
    return { success: false, error: error.response?.data?.message };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Request fund withdrawal
 */
export const requestWithdrawal = (data) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.post('/wallets/withdraw', data);
    if (response.data?.success) {
      // Refresh wallet details and transactions after withdrawal request
      dispatch(fetchWalletDetail(data.walletId));
      dispatch(getWalletTransactions({ walletId: data.walletId }));
      return { success: true };
    }
  } catch (error) {
    logger.error('Withdrawal request failed', error);
    dispatch(setError(error.response?.data?.message || 'Withdrawal failed'));
    return { success: false, error: error.response?.data?.message };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Create Razorpay Order for Recharge
 */
export const createWalletPaymentOrder = (data) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.post('/wallets/payment/create-order', data);
    return response.data;
  } catch (error) {
    logger.error('Failed to create payment order', error);
    dispatch(setError(error.response?.data?.message || 'Payment order failed'));
    return { success: false };
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Verify Razorpay Signature for Recharge
 */
export const verifyWalletPayment = (data) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.post('/wallets/payment/verify', data);
    if (response.data?.success) {
      dispatch(fetchAllWallets());
      return { success: true };
    }
  } catch (error) {
    logger.error('Payment verification failed', error);
    dispatch(setError(error.response?.data?.message || 'Verification failed'));
    return { success: false };
  } finally {
    dispatch(setLoading(false));
  }
};

export default walletSlice.reducer;
